import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { VisualNavigationType } from 'corgi-types';
import { backgroundColor, borderRadius, overflow } from 'styled-system';

import { containerWithBackgroundStyles } from 'common/components/ContainerWithBackground';
import { FlexAlignItems, Grid } from 'common/components/Grid';
import SkeletonLoader from 'common/components/SkeletonLoader';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import { useLoadVisualNavigation } from 'common/hooks/visual-navigation/data';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { DyTrackingGA4Props } from 'common/tracking/getDyTrackingGA4Params';
import type { SeeAllLinkProp, UseLoadVisualNavigationArgs } from 'common/types/visual-navigation';
import Placeholder from './Placeholder';
import { VerticalCategoryGrid } from './VerticalCategoryGrid';
import { VisualNavigation } from './VisualNavigation';

interface CommonVisualNavigationProps extends UseLoadVisualNavigationArgs, SeeAllLinkProp {
  title?: string;
  titleTranslationKey?: string;
  subtitleTranslationKey?: string;
  forceVisibleSlidesCount?: number;
  tracking?: DyTrackingGA4Props;
  withBackground?: boolean;
  imageRatio?: number;
  isGridLayout?: boolean;
}

const UnstyledVisualNavigationGrid = styled(Grid)<any>`
  ${overflow};
`;

const visualNavigationGridStyles: ComponentProps<typeof UnstyledVisualNavigationGrid> = {
  py: 'sp_0',
  px: 'sp_0',
  overflow: 'hidden',
  mb: ['sp_16', 'sp_24', null, 'sp_32'],
  alignItems: FlexAlignItems.CENTER,
};

const VisualNavigationGrid = (props: ComponentProps<typeof UnstyledVisualNavigationGrid>) => (
  <UnstyledVisualNavigationGrid {...visualNavigationGridStyles} {...props} />
);

const UnstyledSyledContainerWithBackground = styled(VisualNavigationGrid)<any>`
  ${backgroundColor};
  ${borderRadius};
`;

const StyledContainerWithBackground = (
  props: ComponentProps<typeof UnstyledSyledContainerWithBackground>
) => (
  <UnstyledSyledContainerWithBackground
    {...visualNavigationGridStyles}
    {...containerWithBackgroundStyles}
    mb="sp_0"
    {...props}
  />
);

// eslint-disable-next-line complexity
const CommonVisualNavigation = ({
  title,
  titleTranslationKey,
  subtitleTranslationKey,
  seeAllLink,
  forceVisibleSlidesCount,
  tracking,
  withBackground,
  imageRatio,
  isGridLayout,
  type = VisualNavigationType.Default,
  urlParams = '',
  ...props
}: CommonVisualNavigationProps) => {
  const { t } = useLocalization();
  const { items, loaded, error } = useLoadVisualNavigation({ type, urlParams, ...props });
  const isVerticalNavigation = useFeatureFlagIsOn(GrowthbookFlag.VerticalCategoryNavigation);
  const isTitleAvailable = Boolean(title || titleTranslationKey);
  const isSubtitleAvailable = Boolean(subtitleTranslationKey);
  const visualNavigationTitle = titleTranslationKey ? t(titleTranslationKey) : title;

  if (error) {
    return null;
  }

  const VisualNavigationContainer = withBackground
    ? StyledContainerWithBackground
    : VisualNavigationGrid;

  const subtitle = !!subtitleTranslationKey ? t(subtitleTranslationKey) : '';

  return (
    <div data-corgi-id="common-visual-navigation">
      <SkeletonLoader
        ready={loaded}
        placeholder={
          <Placeholder
            withBackground={withBackground}
            showLoadingAnimation
            showTitle={isTitleAvailable}
            showSubtitle={isSubtitleAvailable}
            forceVisibleSlidesCount={forceVisibleSlidesCount}
          />
        }
      >
        {items?.length > 0 ? (
          isVerticalNavigation && isGridLayout ? (
            <VerticalCategoryGrid
              items={items}
              subtitle={subtitle}
              {...(seeAllLink ? { seeAllLink } : {})}
              {...(isTitleAvailable ? { title: visualNavigationTitle } : {})}
              {...(tracking ? { tracking } : {})}
            />
          ) : (
            <VisualNavigationContainer
              bg={withBackground ? 'unset' : 'sys.neutral.background.default'}
              data-testid="visual-navigation-container"
            >
              <VisualNavigation
                items={items}
                forceVisibleSlidesCount={forceVisibleSlidesCount}
                subtitle={subtitle}
                withBackground={withBackground}
                imageRatio={imageRatio}
                {...(seeAllLink ? { seeAllLink } : {})}
                {...(isTitleAvailable ? { title: visualNavigationTitle } : {})}
                {...(tracking ? { tracking } : {})}
              />
            </VisualNavigationContainer>
          )
        ) : null}
      </SkeletonLoader>
    </div>
  );
};

export default CommonVisualNavigation;
